import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
// Redux
import { connect } from 'react-redux';
// Redux Form
import {
    Field, reduxForm, formValueSelector, change,
    getFormSyncErrors, submit as submitForm, getFormValues
} from 'redux-form';
import {
    Button,
    FormGroup,
    FormControl,
} from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

// Components
import Loader from '../../Loader';
// Helpers
import validateStripe from './validateStripe';
import submit from './submit';
import { isEuropeCountry } from '../../../helpers/europeCountryHelpers';
import generateStripePayoutToken from '../../../helpers/generateStripePayoutToken';
// Redux actions
import { startPayoutLoading, stopPayoutLoading } from '../../../actions/Payout/addPayoutAction';
// Locale
import messages from '../../../locale/jsonMessages';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import cs from '../../../components/commonStyle.css';
import s from '../Payout.css';

class Stripe extends Component {
    static propTypes = {
        handleSubmit: PropTypes.any.isRequired,
        previousPage: PropTypes.any.isRequired,
        siteName: PropTypes.string.isRequired,
        formatMessage: PropTypes.any,
    };

    constructor(props) {
        super(props);
        this.handleSubmitAction = this.handleSubmitAction.bind(this);
    };

    static defaultProps = {
        businessType: 'individual'
    };

    renderField = ({ input, label, type, meta: { touched, error, dirty }, placeHolder, className, maxLength, isRequiredInput }) => {
        const { formatMessage } = this.props.intl;
        return (
            <FormGroup className={cx(cs.noMargin, className)}>
                <label className='commonLabelText'><span>{label}{isRequiredInput && <sup className='isRequiredStar'>*</sup>}</span></label>
                <FormControl
                    {...input}
                    componentClass="input"
                    className='commonInputField'
                    placeholder={placeHolder}
                    maxLength={maxLength}
                />
                {touched && error && <span className='errorMessageText'>{formatMessage(error)}</span>}
            </FormGroup>
        );
    }

    renderSelectField = ({ input, label, type, meta: { touched, error, dirty }, children, placeHolder, isRequiredInput }) => {
        const { formatMessage } = this.props.intl;
        return (
            <FormGroup className={cs.noMargin}>
                <label className='commonLabelText'><span>{label}{isRequiredInput && <sup className='isRequiredStar'>*</sup>}</span></label>
                <FormControl
                    {...input}
                    componentClass="select"
                    className='commonSelectField'
                    placeholder={placeHolder}
                >
                    {children}
                </FormControl>
                {touched && error && <span className='errorMessageText'>{formatMessage(error)}</span>}
            </FormGroup>
        );
    }

    async handleSubmitAction(event) {
        event.preventDefault();
        const { stripe, change, handleSubmit } = this.props;
        const { formValues, errors, startPayoutLoading, stopPayoutLoading } = this.props;
        let accountToken, personToken;

        if (errors && JSON.stringify(errors) !== '{}') { // If any error trigger the submit action to show the error messages
            await handleSubmit();
        }

        if ((!errors || (errors && JSON.stringify(errors) === '{}')) && formValues && stripe) {
            await startPayoutLoading();
            const generateTokens = await generateStripePayoutToken(stripe, formValues);
            if (generateTokens && generateTokens.status === 200) {
                accountToken = generateTokens.result && generateTokens.result.accountToken;
                personToken = generateTokens.result && generateTokens.result.personToken || null;
                await change('isTokenGenerated', true);
                await change('accountToken', accountToken);
                await change('personToken', personToken);
                await handleSubmit();
            } else {
                showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: generateTokens.errorMessage })
                await stopPayoutLoading();
            }
            return;
        }
    }

    render() {
        const { handleSubmit, pristine, previousPage, submitting, error } = this.props;
        const { base, availableCurrencies, siteName, payoutLoading, businessType, payoutCountry } = this.props;
        const { formatMessage } = this.props.intl;

        return (
            <div className={cx(cs.commonBorderSection, 'whiteBgColor')}>
                <form onSubmit={handleSubmit}>
                    <h3 className={cx(cs.commonTotalText, cs.paddingBottom4)}>{formatMessage(messages.addPayout)}</h3>
                    <div className="oneColumnGrid">
                        <Field
                            name="businessType"
                            component={this.renderSelectField}
                            label={formatMessage(messages.payoutType)}
                        >
                            <option value="individual">{formatMessage(messages.payoutIndividual)}</option>
                            <option value="company">{formatMessage(messages.payoutCompany)}</option>
                        </Field>
                        <div className={cx(businessType === 'individual' ? 'twoColumnGrid' : 'oneColumnGrid')}>
                            <Field
                                name="firstname"
                                component={this.renderField}
                                label={(businessType === 'individual' ? formatMessage(messages.payoutFirstName) : formatMessage(messages.payoutCompanyName))}
                                placeHolder={(businessType === 'individual' ? formatMessage(messages.payoutFirstName) : formatMessage(messages.payoutCompanyName))}
                                maxLength={255}
                                isRequiredInput={true}
                            />
                            {
                                businessType && businessType === 'individual' &&
                                <Field
                                    name="lastname"
                                    component={this.renderField}
                                    label={formatMessage(messages.payoutLastName)}
                                    placeHolder={formatMessage(messages.payoutLastName)}
                                    maxLength={255}
                                    isRequiredInput={true}
                                />
                            }
                        </div>
                        {
                            payoutCountry && ['US', 'CA', 'GB', 'AU', 'GA'].indexOf(payoutCountry) !== -1 &&
                            <Field
                                name="routingNumber"
                                component={this.renderField}
                                label={payoutCountry === 'GB' ? formatMessage(messages.payoutSortNumber) : formatMessage(messages.payoutRouting)}
                                placeHolder={payoutCountry === 'CA' ? "eg: 11000-000" : (payoutCountry === 'GB' ? "eg: 108800" : (payoutCountry === 'AU' ? "eg: 110000" : "eg: 110000000"))}
                                maxLength={50}
                                isRequiredInput={true}
                            />
                        }
                        <Field
                            name="accountNumber"
                            component={this.renderField}
                            label={(isEuropeCountry(payoutCountry) || payoutCountry === 'CR') ? formatMessage(messages.ibanNumber) : formatMessage(messages.accountNumber)}
                            placeHolder={isEuropeCountry(payoutCountry) ? `eg: ${payoutCountry}89370400440532013000` : (payoutCountry === 'GB' ? "eg: 00012345" : (payoutCountry === 'AU' ? "eg: 000123456" : (payoutCountry === 'CR' ? "CR04010212367856709123" : "eg: 000123456789")))}
                            maxLength={50}
                            isRequiredInput={true}
                        />
                        <Field
                            name="confirmAccountNumber"
                            component={this.renderField}
                            label={(isEuropeCountry(payoutCountry) || payoutCountry === 'CR') ? formatMessage(messages.confirmIbanNumber) : formatMessage(messages.confirmAccountNumber)}
                            placeHolder={isEuropeCountry(payoutCountry) ? `eg: ${payoutCountry}89370400440532013000` : (payoutCountry === 'GB' ? "eg: 00012345" : (payoutCountry === 'AU' ? "eg: 000123456" : (payoutCountry === 'CR' ? "CR04010212367856709123" : "eg: 000123456789")))}
                            maxLength={50}
                            isRequiredInput={true}
                        />
                        {
                            payoutCountry && payoutCountry === 'US' && businessType && businessType === 'individual' &&
                            <Field
                                name="ssn4Digits"
                                component={this.renderField}
                                label={formatMessage(messages.ssn4Digits)}
                                placeHolder={"1234"}
                                maxLength={4}
                                isRequiredInput={true}
                            />
                        }
                    </div>
                    <p className={cx(cs.commonMediumText, cs.paddingBottom2, cs.spaceTop2)}>{formatMessage(messages.payoutStripeInfo)}</p>
                    <p className={cs.commonMediumText}>
                        {formatMessage(messages.stripeTokenInfo1)}{' '}
                        <a href={'https://stripe.com/connect-account/legal'} target={'_blank'} className={cs.siteLinkColor}>{formatMessage(messages.stripeTokenInfo2)}</a>.
                    </p>
                    <div className={cx('doubleBtnFlexEnd', cs.spaceTop4)}>
                        <Button className={cx(cs.btnPrimaryBorder)} onClick={previousPage}>
                            <FormattedMessage {...messages.back} />
                        </Button>
                        <Loader
                            type={'button'}
                            buttonType={'button'}
                            className={cx(cs.btnPrimary, 'loaderButtonFlex')}
                            disabled={pristine || submitting || error || payoutLoading}
                            show={payoutLoading}
                            label={formatMessage(messages.finish)}
                            handleClick={this.handleSubmitAction}
                        />
                    </div>
                </form>
            </div>
        );
    }
}

Stripe = reduxForm({
    form: 'PayoutForm', // a unique name for this form
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate: validateStripe,
    onSubmit: submit
})(Stripe);

const selector = formValueSelector('PayoutForm');

const mapState = (state) => ({
    siteName: state.siteSettings.data.siteName,
    availableCurrencies: state.currency.availableCurrencies,
    base: state.currency.base,
    payoutLoading: state.reservation.payoutLoading,
    businessType: selector(state, 'businessType'),
    payoutCountry: selector(state, 'country'),
    formValues: getFormValues('PayoutForm')(state),
    errors: getFormSyncErrors('PayoutForm')(state)
});

const mapDispatch = {
    submitForm,
    change,
    startPayoutLoading,
    stopPayoutLoading
};

export default injectIntl(withStyles(cs, s)(connect(mapState, mapDispatch)(Stripe)));